/**
 * Page scaffolding — scrollable content column (max ~1280px) used by every
 * portal screen.
 */
import type { ReactNode } from 'react'
import { ScrollView, Stack, Text, XStack, YStack } from 'tamagui'

import { useUiPrefs } from '../i18n'
import { LiveBackground } from './LiveBackground'
import { prefersReducedMotion } from './motion'

export function Page({
  title,
  subtitle,
  action,
  children,
  maxWidth = 1280,
  scroll = true,
}: {
  title?: string
  subtitle?: string
  action?: ReactNode
  children: ReactNode
  maxWidth?: number
  /** false: no ScrollView — use when a child (e.g. a virtualized list) must be
   *  its own scroller (FlashList/FlatList can't virtualize inside an outer
   *  ScrollView — it just measures "infinite" and renders everything). */
  scroll?: boolean
}) {
  const { liveBg } = useUiPrefs()
  // Screen transition: every portal screen mounts with a springy fade + rise.
  // Transform/opacity only — CSS on web, native driver on mobile;
  // reduce-motion mounts in place.
  const content = (
    <YStack
      width="100%"
      maxWidth={maxWidth}
      padding={24}
      gap={20}
      flex={scroll ? undefined : 1}
      animation={prefersReducedMotion() ? undefined : 'bouncy'}
      enterStyle={{ opacity: 0, y: 22, scale: 0.985 }}
      opacity={1}
      y={0}
      scale={1}
    >
      {(title || action) && (
        <XStack alignItems="flex-start" justifyContent="space-between" gap={12} flexWrap="wrap">
          <YStack gap={4} flexShrink={1}>
            {title && (
              <Text fontSize={26} fontWeight="800" color="$color" letterSpacing={-0.4}>
                {title}
              </Text>
            )}
            {subtitle && (
              <Text fontSize={14} color="$muted">
                {subtitle}
              </Text>
            )}
          </YStack>
          {action}
        </XStack>
      )}
      {children}
    </YStack>
  )
  return (
    // Live-wallpaper layer sits behind a transparent scroll view; cards are
    // opaque, so content always reads on top of the drifting blobs.
    <Stack flex={1} backgroundColor="$bgLayout" position="relative">
      {liveBg && <LiveBackground />}
      {scroll ? (
        <ScrollView flex={1} backgroundColor="transparent" contentContainerStyle={{ alignItems: 'center' }}>
          {content}
        </ScrollView>
      ) : (
        <Stack flex={1} alignItems="center">
          {content}
        </Stack>
      )}
    </Stack>
  )
}
